import {
  Box,
  Flex,
  FormControl,
  IconButton,
  Modal,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Textarea,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  ModalCloseButton,
} from '@chakra-ui/react';
import {
  productApprovalsApi,
  ProductApprovalState,
  ChangeStateRequest,
} from '@texas/api/endpoints/productApprovalsApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { defaultIconSize, Icons } from '@texas/components/shared/Icons';
import { fadeInLeftAnimation } from '@texas/resources/animations/animations';
import { ReactSelectOption, ServerError } from '@texas/types';
import { request } from '@texas/utils/helpers/httpHelpers';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ProductApprovalInfo } from '../shared/ProductApprovalInfo';
import { AutoGrowTextareaContainer } from '@texas/components/shared/form/AutoGrowTextareaContainer';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { VerifyButton } from '@texas/components/shared/verifyButton/VerifyButton';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { FormCounter } from '@texas/components/shared/form/FormCounter';
import { userApi } from '@texas/api/endpoints/userApi';
import {
  formatGroupLabel,
  TexasSelect,
} from '@texas/components/shared/form/TexasSelect';
import { customerUsersApi } from '@texas/api/endpoints/customerUsersApi';
import { GetGroupedOptions } from './shared';

export function CancelModal({
  productApprovalId,
  onCancel,
}: {
  productApprovalId: number;
  onCancel: () => void;
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { t } = useTranslation();

  return (
    <>
      <Tooltip label={t('productApproval.cancelTooltip')}>
        <IconButton
          variant="texas-light"
          onClick={onOpen}
          size="sm"
          icon={<Icons.MinusCircleOutline boxSize="4" />}
          aria-label={t('productApproval.cancelPa')}
        />
      </Tooltip>
      <Modal
        isCentered={true}
        size="6xl"
        isOpen={isOpen}
        onClose={onClose}
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalInner
            onCancel={onCancel}
            productApprovalId={productApprovalId}
            onClose={onClose}
          />
        </ModalContent>
      </Modal>
    </>
  );
}

interface ModalInnerProps {
  productApprovalId: number;
  onCancel: () => void;
  onClose: () => void;
}

function ModalInner({ productApprovalId, onClose, onCancel }: ModalInnerProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const {
    data: productApproval,
    refetch: refetchProductApproval,
    error: productApprovalError,
    loading: productApprovalLoading,
  } = useApiResource(productApprovalsApi.get);

  const {
    data: allUsers,
    refetch: refetchAllUsers,
    loading: loadingAllUsers,
  } = useApiResource(userApi.getAllInternal);

  const {
    data: customerUsers,
    refetch: refetchCustomerUsers,
    loading: loadingCustomerUsers,
  } = useApiResource(customerUsersApi.getInternalUsersByPaId);

  const {
    request: revertRequest,
    loading,
    error,
  } = useApiRequest(productApprovalsApi.cancel);

  const {
    watch,
    handleSubmit,
    register,
    formState: { errors, isValid },
    control,
  } = useForm<ChangeStateRequest>({
    defaultValues: { note: '', assignedUserIds: [] },
  });

  const watchNote = watch('note');

  const onSubmit = async (data: ChangeStateRequest) =>
    await request(
      revertRequest,
      [productApprovalId, data],
      () => {
        toast({
          title: t('productApproval.cancelled'),
          status: 'success',
          isClosable: true,
        });
        onCancel();
        onClose();
      },
      (error: ServerError) => {
        toast({
          title: t('productApproval.failedToCancel'),
          description: error.message,
          status: 'error',
          isClosable: true,
        });
      },
    );

  useEffect(() => {
    refetchProductApproval(productApprovalId);
    refetchAllUsers();
  }, [productApprovalId, refetchProductApproval, refetchAllUsers]);

  useEffect(() => {
    if (productApproval) {
      refetchCustomerUsers(productApproval.id);
    }
  }, [productApproval, refetchCustomerUsers]);

  return (
    <>
      <ModalHeader
        borderBottom="1px solid"
        borderColor="texas.bg.700"
        _light={{ borderColor: 'gray.50', color: 'black' }}
      >
        {t('general.cancel')}
      </ModalHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        {productApprovalError && <ErrorDetails error={productApprovalError} />}
        {error && <ErrorDetails error={error} />}

        <Flex>
          <Box
            minW="35%"
            maxW="35%"
            p={4}
            bg="texas.bg.700"
            _light={{ bg: 'gray.50' }}
          >
            {productApprovalLoading && <Spinner />}
            {productApproval && (
              <>
                <Flex
                  animation={fadeInLeftAnimation(600)}
                  alignItems="center"
                  gap={1}
                >
                  <Text pb={2} fontFamily="Barlow" fontSize="xl">
                    {t('productApproval.willBeCancelled')}
                  </Text>
                  <Icons.ArrowDownRight boxSize="4" />
                </Flex>
                <ProductApprovalInfo
                  pa={productApproval}
                  state={ProductApprovalState.Cancelled}
                />
              </>
            )}
          </Box>

          <Flex flexDir="column" py="12" flexGrow={1} p={4} gap={2}>
            <FormControl>
              <TexasFormLabel>{t('productApproval.reason')}</TexasFormLabel>
              <AutoGrowTextareaContainer value={watch('note')}>
                <Textarea
                  {...register('note', {
                    maxLength: {
                      value: 200,
                      message: t('errors.maxLength', { count: 200 }),
                    },
                  })}
                  minH="28"
                  height="auto"
                  padding={1}
                  width="100%"
                  borderRadius={3}
                  borderColor="gray"
                  _hover={{ borderColor: 'hsl(0, 0%, 70%)' }}
                />
              </AutoGrowTextareaContainer>
              <FormCounter length={watchNote.length} maxLength={200} />
              <ErrorLabel text={errors.note?.message} />
            </FormControl>
            <FormControl>
              <TexasFormLabel>
                {t('productApproval.assignedUsers')}
              </TexasFormLabel>
              <Controller
                name="assignedUserIds"
                control={control}
                render={({ field }) => (
                  <TexasSelect
                    {...field}
                    isLoading={loadingAllUsers || loadingCustomerUsers}
                    isClearable={true}
                    closeMenuOnSelect={false}
                    isMulti={true}
                    formatGroupLabel={formatGroupLabel}
                    value={field.value.map<ReactSelectOption>((x) => ({
                      label: allUsers?.find((p) => p.id === x)?.name ?? '',
                      value: x,
                    }))}
                    onChange={(e) => field.onChange(e.map((x) => x.value))}
                    options={GetGroupedOptions(customerUsers, allUsers)}
                  />
                )}
              />
              <Text pl={2} variant="small">
                {t('productApproval.taskInfo')}
              </Text>
            </FormControl>
            <Flex ml="auto" mt="auto">
              <VerifyButton
                tooltipLabel={t('productApproval.cancelTooltip')}
                buttonProps={{
                  isDisabled: !isValid,
                  isLoading: loading,
                  leftIcon: (
                    <Icons.MinusCircleOutline boxSize={defaultIconSize} />
                  ),
                }}
                onVerified={() => handleSubmit(onSubmit)()}
                label={t('productApproval.cancelPa')}
              />
            </Flex>
          </Flex>
        </Flex>
      </form>
    </>
  );
}
